/**
 * SVG statique de la zone de travail pour un `programme_construction`.
 *
 * Contient :
 *   - une grille légère en arrière-plan (espace "1 cm" selon pxPerCm) pour
 *     aider l'élève à prendre des repères
 *   - les points nommés (A, B, C…) avec leur label
 *   - les `preShapes` (segments/droites/cercles pré-tracés fournis par le
 *     programme — figure initiale)
 *   - le reste de la viewBox = espace blanc où l'élève trace sa construction
 *
 * Rendu en noir/gris (pas de couleur) — les couleurs des tâches apparaissent
 * uniquement dans les chips de la liste des étapes au-dessus.
 *
 * Props :
 *   - viewBox : { w, h } — dimensions logiques du SVG (défaut 600×400)
 *   - pxPerCm : int — pas de la grille (défaut 30)
 *   - points  : [{ name, x, y }]
 *   - preShapes : [{ type: 'line'|'segment'|'circle', from?, to?, cx?, cy?, r? }]
 */

const LINE_COLOR = '#1e293b';   // ink-800
const POINT_COLOR = '#0f172a';  // ink-900
const GRID_COLOR = '#e2e8f0';   // ink-200 (légère)

const PrintConstructionFigure = ({
    viewBox = { w: 600, h: 400 },
    pxPerCm = 30,
    points = [],
    preShapes = [],
    // Corrigé uniquement : formes de solution [{ color, shape }] dessinées
    // par-dessus la scène — la zone de travail de l'exercice reste vierge.
    solutions = [],
}) => {
    const { w, h } = viewBox;
    // Index des points par nom pour résoudre les from/to des preShapes
    const byName = Object.fromEntries(points.map((p) => [p.name, p]));

    const resolve = (ref) => {
        // ref peut être un nom ("A") ou un objet { x, y }
        if (!ref) return null;
        if (typeof ref === 'string') return byName[ref] || null;
        if (typeof ref === 'object' && 'x' in ref && 'y' in ref) return ref;
        return null;
    };

    return (
        <svg
            viewBox={`0 0 ${w} ${h}`}
            className="ep-pc-figure"
            preserveAspectRatio="xMidYMid meet"
        >
            {/* Grille — pattern répété */}
            <defs>
                <pattern
                    id="ep-pc-grid"
                    width={pxPerCm}
                    height={pxPerCm}
                    patternUnits="userSpaceOnUse"
                >
                    <path
                        d={`M ${pxPerCm} 0 L 0 0 0 ${pxPerCm}`}
                        fill="none"
                        stroke={GRID_COLOR}
                        strokeWidth="0.8"
                    />
                </pattern>
            </defs>
            <rect width={w} height={h} fill="url(#ep-pc-grid)" />

            {/* preShapes : figure initiale fournie par le programme */}
            {preShapes.map((shape, i) => {
                if (shape.type === 'line' || shape.type === 'segment') {
                    const a = resolve(shape.from);
                    const b = resolve(shape.to);
                    if (!a || !b) return null;
                    return (
                        <line
                            key={`ps-${i}`}
                            x1={a.x}
                            y1={a.y}
                            x2={b.x}
                            y2={b.y}
                            stroke={LINE_COLOR}
                            strokeWidth={1.8}
                        />
                    );
                }
                if (shape.type === 'circle') {
                    const c = resolve(shape.center || shape.from);
                    if (!c || shape.r == null) return null;
                    return (
                        <circle
                            key={`ps-${i}`}
                            cx={c.x}
                            cy={c.y}
                            r={shape.r}
                            stroke={LINE_COLOR}
                            strokeWidth={1.5}
                            fill="none"
                        />
                    );
                }
                return null;
            })}

            {/* Solutions (corrigé uniquement) : formes attendues, colorées par
                tâche. `shape` sans champ `type` = cercle (compat historique). */}
            {solutions.map(({ color, shape, pointsOnly }, i) => {
                if (!shape) return null;
                const stroke = color || '#1D9E75';
                if (shape.type === 'line' || shape.type === 'segment') {
                    const a = resolve(shape.p1);
                    const b = resolve(shape.p2);
                    if (!a || !b) return null;
                    // `pointsOnly` : la tâche fait cliquer deux points sans tracer
                    // le trait qui les joint — on n'imprime alors que les extrémités.
                    if (pointsOnly) {
                        return (
                            <g key={`sol-${i}`}>
                                <circle cx={a.x} cy={a.y} r={4.5} fill={stroke} />
                                <circle cx={b.x} cy={b.y} r={4.5} fill={stroke} />
                            </g>
                        );
                    }
                    return (
                        <line
                            key={`sol-${i}`}
                            x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                            stroke={stroke}
                            strokeWidth={2.4}
                        />
                    );
                }
                if (shape.type === 'ray') {
                    const from = resolve(shape.from);
                    if (!from || shape.angleDeg == null) return null;
                    // Trait de `from` jusqu'au bord de la viewBox selon l'angle
                    const rad = (shape.angleDeg * Math.PI) / 180;
                    const dx = Math.cos(rad);
                    const dy = Math.sin(rad);
                    const tMax = Math.max(w, h) * 2;
                    const ex = Math.max(0, Math.min(w, from.x + dx * tMax));
                    const ey = Math.max(0, Math.min(h, from.y + dy * tMax));
                    return (
                        <g key={`sol-${i}`}>
                            <line
                                x1={from.x} y1={from.y} x2={ex} y2={ey}
                                stroke={stroke}
                                strokeWidth={2.4}
                            />
                            {shape.labelEnd && (
                                <text
                                    x={from.x + dx * 60 + 6}
                                    y={from.y + dy * 60 - 6}
                                    fontSize="13"
                                    fontWeight="700"
                                    fill={stroke}
                                >
                                    {shape.labelEnd}
                                </text>
                            )}
                        </g>
                    );
                }
                if (shape.cx != null && shape.cy != null && shape.r != null) {
                    return (
                        <circle
                            key={`sol-${i}`}
                            cx={shape.cx} cy={shape.cy} r={shape.r}
                            stroke={stroke}
                            strokeWidth={2.4}
                            fill="none"
                        />
                    );
                }
                return null;
            })}

            {/* Points nommés */}
            {points.map((p, i) => (
                <g key={`pt-${i}`}>
                    <circle cx={p.x} cy={p.y} r={3.5} fill={POINT_COLOR} />
                    <text
                        x={p.x + 8}
                        y={p.y - 8}
                        fontSize="14"
                        fontWeight="700"
                        fill={POINT_COLOR}
                        fontFamily="'Fraunces', Georgia, serif"
                    >
                        {p.name}
                    </text>
                </g>
            ))}
        </svg>
    );
};

export default PrintConstructionFigure;
